import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import PropTypes from "prop-types";

export default function InternalPostStatusDialog({
  open,
  onClose,
  baseUrl,
  masterId,
  programName,
  examDate,
  examSlotId,
  slotDescription,
  status,
  title,
  buttonLabel,
  modifiedBy,
  showSnackbar,
}) {
  const [studentRollNo, setStudentRollNo] = useState("");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStudentRollNo("");
    setStudents([]);
  }, [open, status]);

  const fetchStudent = async (value) => {
    const searchValue = String(value || "").trim();
    if (!searchValue) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${baseUrl}/${masterId}/seating-plan/search-student`, {
        params: {
          from_exam_date: examDate,
          exam_slot_id: examSlotId,
          value: searchValue,
        },
      });
      const items = data.data?.items || [];
      setStudents(items.map((item) => ({ ...item, id: item.room_allocation_id })));
      if (!items.length) showSnackbar("No matching student found", "warning");
    } catch (error) {
      setStudents([]);
      showSnackbar(error.response?.data?.message || "Error fetching student", "error");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (row) => {
    const nextStatus = status === "AB" && row.attendance_status === "AB" ? "" : status;
    try {
      await axios.put(`${baseUrl}/${masterId}/seating-plan/${row.room_allocation_id}/attendance`, {
        status: nextStatus,
        modified_by: modifiedBy,
      });
      setStudents((current) =>
        current.map((item) =>
          item.room_allocation_id === row.room_allocation_id
            ? { ...item, attendance_status: nextStatus }
            : item
        )
      );
      showSnackbar(
        nextStatus
          ? `Marked ${row.student?.student_name || row.student_rollno} as ${title}`
          : `Unmarked ${row.student?.student_name || row.student_rollno} as Absent`
      );
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || `Failed to ${buttonLabel.toLowerCase()}`,
        "error"
      );
    }
  };

  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 70,
      valueGetter: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
    },
    { field: "student_rollno", headerName: "Roll No", flex: 1 },
    {
      field: "student_name",
      headerName: "Student Name",
      flex: 2,
      valueGetter: ({ row }) => row.student?.student_name || "",
    },
    { field: "attendance_status", headerName: "Attendance", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Button
          variant="contained"
          color="error"
          size="medium"
          disabled={status !== "AB" && row.attendance_status === status}
          onClick={() => updateStatus(row)}
        >
          {status === "AB" && row.attendance_status === "AB" ? "Unmark Absent" : buttonLabel}
        </Button>
      ),
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <span>
            {title} Roll Nos for {programName || ""}
          </span>

          <span>{examDate ? new Date(examDate).toLocaleDateString("en-GB") : ""}</span>

          <span>{slotDescription || ""}</span>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          <TextField
            label="Enter Roll No"
            variant="outlined"
            fullWidth
            value={studentRollNo}
            onChange={(event) => setStudentRollNo(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && fetchStudent(studentRollNo)}
          />
        </Box>
        <Box sx={{ width: "100%", overflowX: "auto", mt: 2 }}>
          <Box sx={{ minWidth: 450 }}>
            <DataGrid
              rows={students}
              columns={columns}
              loading={loading}
              autoHeight
              pageSize={10}
              rowsPerPageOptions={[5, 10, 25, 50]}
              disableSelectionOnClick
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" sx={{ color: "#fff" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

InternalPostStatusDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  baseUrl: PropTypes.string.isRequired,
  masterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  programName: PropTypes.string,
  examDate: PropTypes.string,
  examSlotId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  slotDescription: PropTypes.string,
  status: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  buttonLabel: PropTypes.string.isRequired,
  modifiedBy: PropTypes.string,
  showSnackbar: PropTypes.func.isRequired,
};
